import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";

/* comp imports */
import IngredientsWrapper from "./games-components/IngredientsWrapper";
import AppliancesWrapper from "./games-components/AppliancesWrapper";
import DishTypeNameWrapper from "./games-components/DishTypeNameWrapper";
import PicnicLeftArea from "./games-components/PicnicLeftArea";
import PicnicRightArea from "./games-components/PicnicRightArea";
import PreparationArea from "./games-components/PreparationArea";
import PicnicStoryWrapperStart from "./games-components/PicnicStoryWrapperStart";
import PicnicStoryWrapperEnd from "./games-components/PicnicStoryWrapperEnd";
import PicnicGameModal from "../components/modals/PicnicGameModal";

/* animation imports */
import { elementsEntryExit } from "../assets/js/animations";

/* data imports */
import { dishIngredientsData } from "./game-data/ingredientsData";
import { picnicDisclaimerDataContent } from "./game-data/picnicDisclaimerData";

import "bootstrap/dist/css/bootstrap.min.css";
import "bootstrap/dist/js/bootstrap.bundle.min.js";
import * as bootstrap from "bootstrap";

const PicnicGame = ({ handleRedirect }) => {
  const [gameDisclaimerAccepted, setGameDisclaimerAccepted] = useState(false);
  const [isGameStart, setIsGameStart] = useState(false);
  const [showStoryStart, setShowStoryStart] = useState(false);
  const [showStoryEnd, setShowStoryEnd] = useState(false);
  const [dishType, setDishType] = useState("");
  const [showTableArrows, setShowTableArrows] = useState(false);
  const [activeDishIngredients, setActiveDishIngredients] = useState([]);
  const [selectedIngredients, setSelectedIngredients] = useState([]);
  const [hoveredIngredient, setHoveredIngredient] = useState("");
  const [dishFlag, setDishFlag] = useState("");
  const [greenFlagItems, setGreenFlagItems] = useState([]);
  const [redFlagItems, setRedFlagItems] = useState([]);
  const [calculateIsActive, setCalculateIsActive] = useState(false);
  const [dishIsPrepared, setDishIsPrepared] = useState(false);

  const [showModal, setShowModal] = useState(false);

  /* dishType examples = pizza, pasta, burger, smoothie  */

  const dishTypeHandler = (type) => {
    if (gameDisclaimerAccepted) {
      setDishType(type);
      setShowTableArrows(true);
    }
  };

  const disclaimerAcceptedHandler = () => {
    localStorage.setItem("picnicDisclaimerAccepted", "true");
    setGameDisclaimerAccepted(true);
    setShowStoryStart(true);
  };

  useEffect(() => {
    if (dishType !== "") {
      let tempIngredientsData = dishIngredientsData
        .find((item) => item.name === dishType)
        .data.map((item) => item);
      if (tempIngredientsData.length > 0) {
        setActiveDishIngredients(tempIngredientsData);
      }
    }
  }, [dishType]);

  const gameResetHandler = () => {
    setDishType("");
    setActiveDishIngredients([]);
    setSelectedIngredients([]);
    setDishFlag("");
    setGreenFlagItems([]);
    setRedFlagItems([]);
    setCalculateIsActive(false);
    setDishIsPrepared(false);
    setShowTableArrows(false);
  };

  const handleIngredientSelection = (ingredient) => {
    if (!ingredient || Object.keys(ingredient).length === 0) return;

    const group = ingredient.group;
    const isSelected = selectedIngredients.some(
      (item) => item.name === ingredient.name
    );

    if (isSelected) return;

    const groupCounts = {
      base: 0,
      midlayer: 0,
      addon: 0,
    };

    selectedIngredients.forEach((item) => {
      if (groupCounts.hasOwnProperty(item.group)) {
        groupCounts[item.group]++;
      }
    });

    const canAdd = (() => {
      switch (group) {
        case "base":
          return groupCounts.base === 0;
        case "midlayer":
          return groupCounts.base === 1 && groupCounts.midlayer === 0;
        case "addon":
          return (
            groupCounts.base === 1 &&
            groupCounts.midlayer === 1 &&
            groupCounts.addon === 0
          );
        default:
          return (
            groupCounts.base === 1 &&
            groupCounts.midlayer === 1 &&
            groupCounts.addon === 1
          );
      }
    })();

    if (canAdd) {
      setSelectedIngredients([...selectedIngredients, ingredient]);
      setActiveDishIngredients([...activeDishIngredients]);
    }
  };

  const checkIngredientSelection = (ingredientName) => {
    return selectedIngredients.some((item) => item.name === ingredientName);
  };

  const checkIngredientGroup = (group) => {
    if (group !== "extras") {
      return selectedIngredients.some((item) => item.group === group);
    }
  };

  const ingredientHoverHandler = (itemName) => {
    if (itemName !== "") {
      setHoveredIngredient(itemName);
    } else {
      setHoveredIngredient("");
    }
  };

  const handleProcessBtnClick = (e) => {
    let timer = null;
    let storyTimer = null;

    if (selectedIngredients.length >= 3) {
      document.querySelectorAll(".ingredient-item").forEach((item) => {
        if (
          !item.classList.contains("active") ||
          !item.classList.contains("disabled")
        ) {
          item.classList.add("not-selected");
        }
      });
      e.target.classList.add("disabled");
      setCalculateIsActive(true);
      displayBtnText();
      timer = setTimeout(() => {
        let tempRedFlagArr = [];
        let tempGreenFlagArr = [];
        let tempRedFlagCount = 0;
        let tempGreenFlagCount = 0;

        selectedIngredients.forEach((item) => {
          if (item.flag === "red") {
            tempRedFlagArr.push(item.header);
            tempRedFlagCount++;
          } else {
            tempGreenFlagArr.push(item.header);
            tempGreenFlagCount++;
          }
        });

        setGreenFlagItems(tempGreenFlagArr);
        setRedFlagItems(tempRedFlagArr);

        if (tempRedFlagCount > tempGreenFlagCount) {
          setDishFlag("red");
        } else {
          setDishFlag("green");
        }

        setCalculateIsActive(false);
        setDishIsPrepared(true);
        displayBtnText();
        setShowTableArrows(false);
      }, 4000);

      storyTimer = setTimeout(() => {
        setShowStoryEnd(true);
      }, 6000);
    }

    return () => {
      clearTimeout(timer);
      clearTimeout(storyTimer);
    };
  };

  const displayBtnText = () => {
    if (dishType !== "") {
      let displayText = "";

      switch (dishType) {
        case "pizza":
          displayText = "Bake";
          calculateIsActive && (displayText = "Baking");
          dishIsPrepared && (displayText = "Baked");
          break;
        case "pasta":
          displayText = "Cook";
          calculateIsActive && (displayText = "Cooking");
          dishIsPrepared && (displayText = "Cooked");
          break;
        case "burger":
          displayText = "Grill";
          calculateIsActive && (displayText = "Grilling");
          dishIsPrepared && (displayText = "Grilled");
          break;
        case "smoothie":
          displayText = "Blend";
          calculateIsActive && (displayText = "Blending");
          dishIsPrepared && (displayText = "Blended");
          break;
        default:
          displayText = "";
      }

      return displayText;
    }
  };

  useEffect(() => {
    const accepted =
      localStorage.getItem("picnicDisclaimerAccepted") === "true";
    setGameDisclaimerAccepted(accepted);
    setShowModal(true);
  }, []);

  useEffect(() => {
    if (showModal) {
      const modalElement = document.getElementById("picnicGameModal");
      const modal = new bootstrap.Modal(modalElement);
      modal.show();
    }
  }, [showModal]);

  return (
    <div
      className={`picnic-game-container ${
        isGameStart ? "game-started-bg" : ""
      }`}
    >
      <PicnicLeftArea
        showStoryStart={showStoryStart}
        showStoryEnd={showStoryEnd}
        dishType={dishType}
        calculateIsActive={calculateIsActive}
        dishIsPrepared={dishIsPrepared}
        gameDisclaimerAccepted={gameDisclaimerAccepted}
        isGameStart={isGameStart}
      />
      <div className="picnic-center-area">
        {isGameStart && (
          <>
            {dishType !== "" ? (
              <div className="game-reset-btn" onClick={gameResetHandler}>
                <span className="material-symbols-rounded">frame_reload</span>
              </div>
            ) : null}

            <AppliancesWrapper
              dishTypeHandler={dishTypeHandler}
              dishType={dishType}
              calculateIsActive={calculateIsActive}
            />
            <DishTypeNameWrapper dishType={dishType} />
            <div className="table-area"></div>
            <AnimatePresence>
              {showTableArrows && (
                <motion.div
                  className="table-num-arrows"
                  variants={elementsEntryExit}
                  initial="initial"
                  animate="animate"
                  exit="exit"
                ></motion.div>
              )}
            </AnimatePresence>
            <IngredientsWrapper
              dishType={dishType}
              activeDishIngredients={activeDishIngredients}
              setActiveDishIngredients={setActiveDishIngredients}
              handleIngredientSelection={handleIngredientSelection}
              checkIngredientSelection={checkIngredientSelection}
              checkIngredientGroup={checkIngredientGroup}
              ingredientHoverHandler={ingredientHoverHandler}
            />
            <PreparationArea
              hoveredIngredient={hoveredIngredient}
              dishType={dishType}
              selectedIngredients={selectedIngredients}
              calculateIsActive={calculateIsActive}
              dishIsPrepared={dishIsPrepared}
            />

            {dishType !== "" ? (
              <div className="process-btn-wrapper">
                <div className="process-btn" onClick={handleProcessBtnClick}>
                  {displayBtnText()}
                </div>
              </div>
            ) : null}
          </>
        )}
      </div>
      <PicnicRightArea
        dishFlag={dishFlag}
        greenFlagItems={greenFlagItems}
        redFlagItems={redFlagItems}
        gameResetHandler={gameResetHandler}
      />
      {showStoryStart && (
        <PicnicStoryWrapperStart
          setShowStoryStart={setShowStoryStart}
          setIsGameStart={setIsGameStart}
          gameDisclaimerAccepted={gameDisclaimerAccepted}
        />
      )}
      {showStoryEnd && (
        <PicnicStoryWrapperEnd
          dishFlag={dishFlag && dishFlag}
          setShowStoryEnd={setShowStoryEnd}
          gameResetHandler={gameResetHandler}
          handleRedirect={handleRedirect}
        />
      )}
      <PicnicGameModal
        picnicDisclaimerDataContent={picnicDisclaimerDataContent}
        disclaimerAcceptedHandler={disclaimerAcceptedHandler}
        gameDisclaimerAccepted={gameDisclaimerAccepted}
      />
    </div>
  );
};

export default PicnicGame;
